import { useEffect, useState, useMemo, useRef, useCallback } from "react"
import { usePropertyContext } from "@/lib/usePropertyContext"
import { useAuth } from "@/lib/auth"
import { useSearchParams, useNavigate } from "react-router"
import { apiClient } from "@/lib/api"
import { CopyConfigModal } from "@/components/CopyConfigModal"
import { useToast } from "@stayframe/ui"
import { usePlanContext } from "@/lib/usePlanContext"

// ─── Research / Enrichment Types ─────────────────────────────────────────────

interface Enrichment {
  description: string
  place_type: string
  seasonality: string
  short_description: string
  why_visit: string
  practical_tip: string
  rain_alternative: string
  best_for: string
  suitable_for: string[]
  tags: string[]
  family_friendly: boolean | null
  accessibility: string | null
  price_range: string | null
  website: string
  phone: string
  instagram_handle: string
  enrichment_confidence: number
}

interface ResearchItem {
  place_id: string
  place_name: string
  area: string
  type: string
  status: string
  opening_hours_applicable: boolean
  opening_hours_summary: string
  new_information: string[]
  enrichment?: Enrichment
  confidence: number
  notes: string
  raw_llm_response?: string
  source_urls: string[]
}

interface ResearchSummary {
  total_places: number
  completed_places: number
  failed_places: number
  skipped_places: number
  places_with_rule_suggestions: number
  places_with_enrichment: number
}

interface EnrichSummary {
  enabled: boolean
  enriched_places: number
  fields_filled: number
  skipped_already_filled: number
  saved: boolean
}

interface ResearchReport {
  run_id: string
  run_status: string
  generated_at: string
  finished_at: string
  summary: ResearchSummary
  auto_enrich?: EnrichSummary | null
  items: ResearchItem[]
  in_progress: boolean
}

// ─── Research Helpers ────────────────────────────────────────────────────────

const ENRICHMENT_FIELDS: Array<{ key: keyof Enrichment; label: string; placeKey: string }> = [
  { key: "description", label: "Description", placeKey: "en.description" },
  { key: "place_type", label: "Type", placeKey: "type" },
  { key: "seasonality", label: "Seasonality", placeKey: "seasonality" },
  { key: "short_description", label: "Short description", placeKey: "en.short" },
  { key: "why_visit", label: "Why visit", placeKey: "en.why" },
  { key: "practical_tip", label: "Practical tip", placeKey: "en.tip" },
  { key: "rain_alternative", label: "Rain alternative", placeKey: "en.rainAlternative" },
  { key: "best_for", label: "Best for", placeKey: "en.bestFor" },
  { key: "suitable_for", label: "Suitable for", placeKey: "suitableFor" },
  { key: "tags", label: "Tags", placeKey: "tags" },
  { key: "family_friendly", label: "Family friendly", placeKey: "familyFriendly" },
  { key: "accessibility", label: "Accessibility", placeKey: "accessibility" },
  { key: "price_range", label: "Price range", placeKey: "price_range" },
  { key: "website", label: "Website", placeKey: "research_sources.official_site" },
]

function countEnrichmentFields(e?: Enrichment): { filled: number; total: number } {
  if (!e) return { filled: 0, total: ENRICHMENT_FIELDS.length }
  let filled = 0
  if (e.description) filled++
  if (e.place_type) filled++
  if (e.seasonality) filled++
  if (e.short_description) filled++
  if (e.why_visit) filled++
  if (e.practical_tip) filled++
  if (e.rain_alternative) filled++
  if (e.best_for) filled++
  if (e.suitable_for?.length) filled++
  if (e.tags?.length) filled++
  if (e.family_friendly !== null) filled++
  if (e.accessibility) filled++
  if (e.price_range) filled++
  if (e.website) filled++
  return { filled, total: ENRICHMENT_FIELDS.length }
}

function getResearchStatusInfo(status: string): { label: string; variant: string } {
  switch (status) {
    case "ok": return { label: "Researched", variant: "healthy" }
    case "llm_error": return { label: "LLM Error", variant: "error" }
    case "llm_invalid_json": return { label: "Parse Error", variant: "error" }
    case "no_usable_sources": return { label: "No Sources", variant: "warning" }
    case "no_search_results": return { label: "No Results", variant: "warning" }
    case "skipped_not_applicable": return { label: "Skipped", variant: "neutral" }
    case "skipped_missing_place": return { label: "Missing", variant: "neutral" }
    default: return { label: status || "Unknown", variant: "neutral" }
  }
}

function formatEnrichmentValue(value: unknown): string {
  if (value === null || value === undefined) return ""
  if (Array.isArray(value)) return value.join(", ")
  if (typeof value === "boolean") return value ? "Yes" : "No"
  return String(value)
}

type AiStatus = "up-to-date" | "needs-enrichment" | "error" | "running" | "queued"

const ERROR_STATUSES = new Set(["llm_error", "llm_invalid_json", "no_usable_sources", "no_search_results"])

function deriveAiStatus(researchItem: ResearchItem | null, inProgress: boolean, progress: Record<string, unknown> | null, isRich = false): AiStatus {
  // Rich quality places are considered fully enriched — no re-enrichment needed
  if (isRich) return "up-to-date"
  if (inProgress && !researchItem) return "queued"
  if (inProgress && researchItem && progress) {
    // If processed count is still growing and this item hasn't been processed yet
    // we can't easily tell — but if item exists it was processed
  }
  if (!researchItem) return "needs-enrichment"
  if (ERROR_STATUSES.has(researchItem.status)) return "error"
  if (researchItem.status === "ok") return "up-to-date"
  // skipped statuses
  return "needs-enrichment"
}

const AI_STATUS_CONFIG: Record<AiStatus, { label: string; variant: string }> = {
  "up-to-date": { label: "Enriched", variant: "healthy" },
  "needs-enrichment": { label: "Not enriched", variant: "neutral" },
  "error": { label: "Error", variant: "error" },
  "running": { label: "Running", variant: "neutral" },
  "queued": { label: "Queued", variant: "neutral" },
}

const AI_STATUS_SORT_PRIORITY: Record<AiStatus, number> = {
  "error": 0,
  "running": 1,
  "queued": 2,
  "needs-enrichment": 3,
  "up-to-date": 4,
}

function getHumanReadableError(status: string): string {
  switch (status) {
    case "llm_error": return "The AI service returned an error. Try running enrichment again."
    case "llm_invalid_json": return "The AI response could not be parsed. You can view the raw output below."
    case "no_usable_sources": return "No web sources could be found for this place. Check the place name and area."
    case "no_search_results": return "No search results found. Try updating the place name or area."
    default: return "An unknown error occurred."
  }
}

// ─── Research State Hook ─────────────────────────────────────────────────────

function useResearchState(propertyId: string | undefined, toast: (msg: string, variant?: "success" | "error" | "warning" | "info") => void, onComplete?: () => void) {
  const [report, setReport] = useState<ResearchReport | null>(null)
  const [inProgress, setInProgress] = useState(false)
  const [progress, setProgress] = useState<Record<string, unknown> | null>(null)
  const [researchLoading, setResearchLoading] = useState(true)
  const [researchError, setResearchError] = useState("")
  const [enrichingPlaceIds, setEnrichingPlaceIds] = useState<Set<string>>(new Set())
  const userInitiatedRef = useRef(false)

  const itemsByPlaceId = useMemo(() => {
    const map = new Map<string, ResearchItem>()
    if (report?.items) {
      for (const item of report.items) {
        map.set(item.place_id, item)
      }
    }
    return map
  }, [report])

  const getItemForPlace = useCallback((placeId: string): ResearchItem | null => {
    return itemsByPlaceId.get(placeId) || null
  }, [itemsByPlaceId])

  const loadResearch = useCallback(() => {
    if (!propertyId) return
    setResearchLoading(true)
    apiClient.getResearch(propertyId).then((res) => {
      if (res.ok) {
        setReport((res.report as unknown as ResearchReport) || null)
        setInProgress(res.in_progress || false)
        setProgress(res.active_progress as Record<string, unknown> | null || null)
      }
      setResearchLoading(false)
    })
  }, [propertyId])

  useEffect(() => {
    loadResearch()
  }, [loadResearch])

  // Auto-continue polling while in progress
  useEffect(() => {
    if (!inProgress || !propertyId) return
    let stopped = false
    const poll = async () => {
      let consecutiveErrors = 0
      while (!stopped) {
        await new Promise(r => setTimeout(r, 5000))
        if (stopped) break
        try {
          const res = await apiClient.getResearch(propertyId)
          if (!res.ok) { consecutiveErrors++; if (consecutiveErrors >= 3) break; continue }
          consecutiveErrors = 0
          if (res.report) setReport(res.report as unknown as ResearchReport)
          if (res.in_progress) {
            setProgress(res.active_progress as Record<string, unknown> | null || null)
            if (userInitiatedRef.current) {
              try { await apiClient.runResearch({ action: "continue" }, propertyId) } catch { /* noop */ }
            }
          } else {
            setInProgress(false)
            setProgress(null)
            setEnrichingPlaceIds(new Set())
            userInitiatedRef.current = false
            loadResearch()
            onComplete?.()
            break
          }
        } catch { consecutiveErrors++; if (consecutiveErrors >= 3) break }
      }
      if (!stopped && consecutiveErrors >= 3) {
        setInProgress(false)
        setEnrichingPlaceIds(new Set())
        userInitiatedRef.current = false
        setResearchError("Enrichment polling stopped due to repeated errors")
        onComplete?.()
      }
    }
    poll()
    return () => { stopped = true }
  }, [inProgress, propertyId, loadResearch])

  const handleRun = useCallback(async (action: string) => {
    if (!propertyId) return
    setResearchError("")
    userInitiatedRef.current = true
    setInProgress(true)
    try {
      const res = await apiClient.runResearch({ action }, propertyId)
      if (!res.ok) {
        setInProgress(false)
        const debugErr = (res as Record<string, unknown>).debug_error
        setResearchError(debugErr ? `${res.error} — Debug: ${debugErr}` : (res.error || "Failed to start enrichment"))
        return
      }
      if (res.progress) setProgress(res.progress as Record<string, unknown>)
    } catch {
      setInProgress(false)
      setResearchError("Failed to start enrichment")
    }
  }, [propertyId])

  const handleStop = useCallback(async () => {
    if (!propertyId) return
    try {
      await apiClient.runResearch({ action: "cancel" }, propertyId)
      setInProgress(false)
      setProgress(null)
      userInitiatedRef.current = false
      toast("Enrichment stopped", "success")
      loadResearch()
    } catch {
      setInProgress(false)
    }
  }, [propertyId, toast, loadResearch])

  const handleEnrichPlace = useCallback(async (placeId: string) => {
    if (!propertyId) return
    setEnrichingPlaceIds(prev => new Set(prev).add(placeId))
    try {
      const res = await apiClient.runResearch({ action: "start", retry_place_ids: [placeId], skip_already_enriched: false }, propertyId)
      if (!res.ok) {
        setEnrichingPlaceIds(prev => { const next = new Set(prev); next.delete(placeId); return next })
        toast(res.error || "Failed to enrich place", "error")
        return
      }
      userInitiatedRef.current = true
      setInProgress(true)
      if (res.progress) setProgress(res.progress as Record<string, unknown>)
    } catch {
      setEnrichingPlaceIds(prev => { const next = new Set(prev); next.delete(placeId); return next })
      toast("Failed to enrich place", "error")
    }
  }, [propertyId, toast])

  // Enrich All cooldown: block batch enrichment for 24 hours after a batch run
  const enrichAllCooldown = useMemo(() => {
    if (!report?.finished_at) return false
    // Only apply cooldown for batch runs (more than 1 place)
    if (report.summary && report.summary.total_places <= 1) return false
    const finished = new Date(report.finished_at).getTime()
    if (Number.isNaN(finished)) return false
    return Date.now() - finished < 24 * 60 * 60 * 1000
  }, [report])

  return {
    report, inProgress, progress, researchLoading, researchError,
    loadResearch, handleRun, handleStop, getItemForPlace, itemsByPlaceId,
    handleEnrichPlace, enrichingPlaceIds, enrichAllCooldown,
  }
}

// ─── Place Types ─────────────────────────────────────────────────────────────

interface Place {
  id: string
  area: string
  type: string
  seasonality?: string
  en?: { name?: string; description?: string; tips?: string[] }
  sv?: { name?: string; description?: string }
  de?: { name?: string; description?: string }
  coordinates?: { lat?: number | null; lng?: number | null }
  host_recommendation?: string
  [key: string]: unknown
}

interface PlaceSearchResult {
  place_id: string
  name: string
  display_name: string
  lat: number | null
  lng: number | null
  type: string
  address?: Record<string, string>
  website?: string | null
}

const PLACE_TYPES = ["restaurant", "cafe", "bar", "beach", "museum", "park", "shop", "attraction", "nature", "activity", "other"]
const SEASONALITY_OPTIONS = ["year-round", "summer", "winter", "shoulder"]
const SUITABLE_FOR_OPTIONS = ["solo", "couples", "groups", "friends", "families", "kids"]
const ACCESSIBILITY_OPTIONS = ["ground_floor", "flat_paths", "flat_sand", "wooden_pier", "paths_gravel", "pier_ladder", "paths_mixed"]

const HOST_REC_HINT = "Write as if telling a friend \u2014 what you love, what to order, when to go. The AI will use your words when recommending this place to guests."

type SortColumn = "name" | "type" | "area" | "quality" | "ai"
type SortDir = "asc" | "desc"

// ─── Completeness Score ─────────────────────────────────────────────────────
// Scores how much AI-relevant data a place has. Weighted by actual usage in
// guide-logic.js: formatPlaceLine (prompt fields) + scorePlaceForParty (ranking fields).

interface CompletenessResult {
  score: number
  max: number
  label: string
  variant: "healthy" | "warning" | "error" | "neutral"
  missing: { field: string; reason: string }[]
}

function computeCompleteness(place: Place): CompletenessResult {
  const missing: { field: string; reason: string }[] = []
  let score = 0
  const max = 11

  // Prompt fields (directly appear in AI context via formatPlaceLine)
  const hr = (place.host_recommendation || "").trim()
  if (hr.length > 20) { score += 2 } else { missing.push({ field: "host_recommendation", reason: "Used by the AI when writing recommendations" }) }
  if (place.en?.description) { score += 1 } else { missing.push({ field: "en.description", reason: "Provides context about this place" }) }
  if ((place.en as Record<string, unknown>)?.short) { score += 1 } else { missing.push({ field: "en.short", reason: "One-liner shown in the AI prompt" }) }
  if ((place.en as Record<string, unknown>)?.why) { score += 1 } else { missing.push({ field: "en.why", reason: "Tells the AI why to recommend this place" }) }
  if ((place.en as Record<string, unknown>)?.tip) { score += 1 } else { missing.push({ field: "en.tip", reason: "Practical tip shown to guests" }) }
  if (place.coordinates?.lat != null && place.coordinates?.lng != null) { score += 1 } else { missing.push({ field: "coordinates", reason: "No location \u2014 the AI can\u2019t calculate distance" }) }

  // Ranking fields (affect which places the AI selects via scorePlaceForParty)
  const suitableFor = place.suitableFor as string[] | undefined
  if (Array.isArray(suitableFor) && suitableFor.length > 0) { score += 1 } else { missing.push({ field: "suitableFor", reason: "Affects which guest types see this place" }) }
  const tags = place.tags as string[] | undefined
  if (Array.isArray(tags) && tags.length > 0) { score += 1 } else { missing.push({ field: "tags", reason: "Helps the AI match to guest preferences" }) }
  if (place.familyFriendly === true || place.familyFriendly === false) { score += 1 } else { missing.push({ field: "familyFriendly", reason: "Major impact on family recommendations (\u00b15 score)" }) }
  if (place.accessibility) { score += 1 } else { missing.push({ field: "accessibility", reason: "Used for families with children and multigenerational groups" }) }

  let label: string
  let variant: CompletenessResult["variant"]
  if (score <= 2) { label = "Minimal"; variant = "error" }
  else if (score <= 5) { label = "Basic"; variant = "warning" }
  else if (score <= 8) { label = "Good"; variant = "neutral" }
  else { label = "Rich"; variant = "healthy" }

  return { score, max, label, variant, missing }
}

// ─── AI Preview ─────────────────────────────────────────────────────────────
// MIRROR OF guide-logic.js:formatPlaceLine — keep in sync
// This produces the exact prompt line the AI sees for a given place.

function formatPlaceLinePreview(place: Place): string {
  const t = (place.en || {}) as Record<string, unknown>
  const bits: string[] = []
  bits.push(`${t.name || "Place"} (${place.area})`)
  if (place.type) bits.push(`type: ${place.type}`)
  if (t.short) bits.push(`what: ${t.short}`)
  if (t.why) bits.push(`why: ${t.why}`)
  if (t.tip) bits.push(`tip: ${t.tip}`)
  const hr = ((place.host_recommendation as string) || "").trim()
  if (hr) bits.push(`host recommendation: ${hr}`)
  const weather = place.weather as Record<string, unknown> | undefined
  if (weather?.windNote) bits.push(`wind: ${weather.windNote}`)
  if (t.rainAlternative) bits.push(`rain alternative: ${t.rainAlternative}`)
  return `- ${bits.join(" | ")}`
}

// Simplified party scoring preview — shows score for each party type
function computePartyScores(place: Place): { solo: number; couple: number; family: number; group: number } {
  const suitableFor = Array.isArray(place.suitableFor) ? (place.suitableFor as string[]).map(s => s.toLowerCase()) : []
  const tags = Array.isArray(place.tags) ? (place.tags as string[]).map(s => s.toLowerCase()) : []
  const bestFor = (((place.en as Record<string, unknown>)?.bestFor as string) || "").toLowerCase()

  let solo = 0
  if (suitableFor.includes("solo")) solo += 3
  if (tags.some(tag => ["walk", "photography", "nature", "calm"].includes(tag))) solo += 1

  let couple = 0
  if (suitableFor.includes("couples")) couple += 4
  if (bestFor.includes("couple") || bestFor.includes("date") || bestFor.includes("romantic")) couple += 2
  if (place.familyFriendly === false) couple += 1

  let group = 0
  if (suitableFor.includes("groups")) group += 4
  if (suitableFor.includes("friends")) group += 2

  let family = 0
  if (place.familyFriendly === true) family += 4
  if (place.familyFriendly === false) family -= 5
  if (suitableFor.includes("families") || suitableFor.includes("kids")) family += 2
  if (tags.some(tag => ["family", "kids", "playground", "gelato"].includes(tag))) family += 1
  if (tags.some(tag => ["date-night", "cocktails", "adults"].includes(tag))) family -= 2

  return { solo, couple, family, group }
}

function placeName(p: Place) {
  return p.en?.name || p.id
}

function placeDesc(p: Place) {
  return p.en?.description || ""
}

function slugify(name: string, existingIds: Set<string>): string {
  const base = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "place"
  if (!existingIds.has(base)) return base
  let i = 2
  while (existingIds.has(`${base}-${i}`)) i++
  return `${base}-${i}`
}

function areaFromAddress(address?: Record<string, string>): string {
  if (!address) return ""
  return address.city || address.town || address.village || address.municipality || address.county || ""
}

// ─── Save Queue ─────────────────────────────────────────────────────────────
// Ensures only one PUT is in-flight at a time. If a save is triggered while
// one is pending, the latest payload is queued and sent when the current completes.

function useSaveQueue(propertyId: string | undefined, toast: (msg: string, variant?: "success" | "error" | "warning" | "info") => void) {
  const inflightRef = useRef(false)
  const pendingRef = useRef<{ places: Place[]; resolve: (ok: boolean) => void } | null>(null)
  const revisionRef = useRef(0)
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null)

  const flush = useCallback(async () => {
    if (inflightRef.current || !pendingRef.current || !propertyId) return
    inflightRef.current = true
    const { places, resolve } = pendingRef.current
    pendingRef.current = null
    revisionRef.current += 1
    const rev = revisionRef.current
    try {
      const res = await apiClient.savePlaces(places as Array<Record<string, unknown>>, propertyId)
      if (res.ok) {
        setLastSavedAt(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }))
        resolve(true)
      } else {
        toast(`Save failed (rev ${rev}): ${res.error || "Unknown error"}`, "error")
        resolve(false)
      }
    } catch {
      toast(`Save failed (rev ${rev}): Network error`, "error")
      resolve(false)
    } finally {
      inflightRef.current = false
      // Flush any queued save
      if (pendingRef.current) flush()
    }
  }, [propertyId, toast])

  const enqueue = useCallback((places: Place[]): Promise<boolean> => {
    return new Promise((resolve) => {
      // If there's already a pending save, replace it (latest wins)
      if (pendingRef.current) {
        pendingRef.current.resolve(true) // Resolve the old one as superseded
      }
      pendingRef.current = { places, resolve }
      flush()
    })
  }, [flush])

  return { enqueue, lastSavedAt }
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export function PlacesPage() {
  const { propertyId, property } = usePropertyContext()
  const { properties } = useAuth()
  const { toast } = useToast()
  const { canAccess } = usePlanContext()
  const navigate = useNavigate()
  const canEnrich = canAccess("research.batch_research")
  const [searchParams, setSearchParams] = useSearchParams()
  const [places, setPlaces] = useState<Place[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [typeFilter, setTypeFilter] = useState("")
  const [areaFilter, setAreaFilter] = useState("")
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null)
  const [sortDir, setSortDir] = useState<SortDir>("asc")
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showCopyModal, setShowCopyModal] = useState(false)
  const [saving, setSaving] = useState(false)
  const [showDrawer, setShowDrawer] = useState(false)

  const { enqueue, lastSavedAt } = useSaveQueue(propertyId, toast)

  // Open drawer from URL param (legacy research redirect)
  useEffect(() => {
    if (searchParams.get("drawer") === "research") {
      setShowDrawer(true)
      setSearchParams((prev) => { prev.delete("drawer"); return prev }, { replace: true })
    }
  }, [searchParams, setSearchParams])

  const loadPlaces = useCallback(() => {
    if (!propertyId) return
    setLoading(true)
    apiClient.getPlaces(propertyId).then((res) => {
      if (res.ok && res.places) {
        setPlaces(res.places as Place[])
      }
      setLoading(false)
    })
  }, [propertyId])

  const research = useResearchState(propertyId, toast, loadPlaces)

  useEffect(() => {
    loadPlaces()
  }, [loadPlaces])

  const types = useMemo(() => {
    const set = new Set<string>()
    for (const p of places) {
      if (p.type) set.add(p.type)
    }
    return Array.from(set).sort()
  }, [places])

  const areas = useMemo(() => {
    const set = new Set<string>()
    for (const p of places) {
      if (p.area) set.add(p.area)
    }
    return Array.from(set).sort()
  }, [places])

  const filtered = useMemo(() => {
    let result = places
    if (search) {
      const q = search.toLowerCase()
      result = result.filter(
        (p) =>
          p.en?.name?.toLowerCase().includes(q) ||
          p.id?.toLowerCase().includes(q) ||
          p.en?.description?.toLowerCase().includes(q) ||
          p.area?.toLowerCase().includes(q),
      )
    }
    if (typeFilter) {
      result = result.filter((p) => p.type === typeFilter)
    }
    if (areaFilter) {
      result = result.filter((p) => p.area === areaFilter)
    }
    if (sortColumn) {
      const dir = sortDir === "asc" ? 1 : -1
      result = [...result].sort((a, b) => {
        let av: string | number = ""
        let bv: string | number = ""
        switch (sortColumn) {
          case "name": av = placeName(a).toLowerCase(); bv = placeName(b).toLowerCase(); break
          case "type": av = a.type || ""; bv = b.type || ""; break
          case "area": av = a.area || ""; bv = b.area || ""; break
          case "quality": av = computeCompleteness(a).score; bv = computeCompleteness(b).score; break
          case "ai": {
            const aStatus = deriveAiStatus(research.getItemForPlace(a.id), research.inProgress, research.progress)
            const bStatus = deriveAiStatus(research.getItemForPlace(b.id), research.inProgress, research.progress)
            av = AI_STATUS_SORT_PRIORITY[aStatus]
            bv = AI_STATUS_SORT_PRIORITY[bStatus]
            break
          }
        }
        if (av < bv) return -1 * dir
        if (av > bv) return 1 * dir
        return 0
      })
    }
    return result
  }, [places, search, typeFilter, areaFilter, sortColumn, sortDir])

  const selected = selectedId ? places.find((p) => p.id === selectedId) || null : null

  // Optimistic save helper: update local state first, rollback on failure
  const optimisticSave = async (nextPlaces: Place[], rollback: Place[]) => {
    setSaving(true)
    setPlaces(nextPlaces)
    const ok = await enqueue(nextPlaces)
    if (!ok) {
      setPlaces(rollback)
    }
    setSaving(false)
    return ok
  }

  const handleDelete = async (placeId: string) => {
    if (!propertyId) return
    const rollback = places
    const remaining = places.filter((p) => p.id !== placeId)
    setSelectedId(null)
    const ok = await optimisticSave(remaining, rollback)
    if (ok) {
      toast("Place removed.", "success")
    }
  }

  const handleAddPlace = async (newPlace: Place) => {
    if (!propertyId) return
    const rollback = places
    const updated = [...places, newPlace]
    const ok = await optimisticSave(updated, rollback)
    if (ok) {
      setShowAddModal(false)
      toast("Place added.", "success")
    }
  }

  const handleUpdatePlace = async (placeId: string, updatedFields: Partial<Place>) => {
    if (!propertyId) return
    const rollback = places
    const updated = places.map((p) => {
      if (p.id !== placeId) return p
      // Spread-merge: preserve all existing fields (including unknown ones),
      // then overwrite only the fields that were edited
      const merged = { ...p, ...updatedFields }
      // Deep-merge the `en` object to avoid losing unedited sub-fields
      if (updatedFields.en) {
        merged.en = { ...p.en, ...updatedFields.en }
      }
      return merged as Place
    })
    const ok = await optimisticSave(updated, rollback)
    if (ok) {
      toast("Place updated.", "success")
    }
    return ok
  }

  const handleSelectRow = (placeId: string) => {
    setSelectedId(placeId === selectedId ? null : placeId)
  }

  const hasActiveFilters = Boolean(search || typeFilter || areaFilter)

  const handleSort = (col: SortColumn) => {
    if (sortColumn === col) {
      setSortDir((d) => d === "asc" ? "desc" : "asc")
    } else {
      setSortColumn(col)
      setSortDir("asc")
    }
  }

  const clearAllFilters = () => { setSearch(""); setTypeFilter(""); setAreaFilter("") }

  return (
    <div>
      <div className="page-header">
        <div className="page-header__row">
          <div>
            <h1 className="page-header__title">Places</h1>
            <p className="page-header__subtitle">
              {places.length} place{places.length !== 1 ? "s" : ""} in guide
              {lastSavedAt && (
                <span className="ml-3 text-muted-foreground">
                  · Saved {lastSavedAt}
                </span>
              )}
            </p>
          </div>
          <div className="page-header__actions">
            {properties.length > 1 && (
              <button
                className="sf-btn sf-btn--ghost sf-btn--sm"
                onClick={() => setShowCopyModal(true)}
                type="button"
              >
                Copy to...
              </button>
            )}
            {canEnrich && (
              <button
                className="sf-btn sf-btn--secondary sf-btn--sm"
                onClick={() => research.handleRun("continue")}
                disabled={research.inProgress || places.length === 0 || research.enrichAllCooldown}
                title={places.length === 0 ? "Add places first" : research.enrichAllCooldown ? "Limited to once per day" : undefined}
              >
                {research.inProgress && !research.enrichingPlaceIds.size ? (
                  <><span className="sf-spinner sf-spinner--xs mr-1.5" />Enriching all...</>
                ) : (
                  "Enrich All"
                )}
              </button>
            )}
            <button
              className="sf-btn sf-btn--primary sf-btn--sm"
              onClick={() => setShowAddModal(true)}
            >
              Add place
            </button>
          </div>
        </div>
      </div>

      {/* Upgrade banner for non-Concierge tiers */}
      {!canEnrich && !loading && places.length > 0 && (
        <div className="pg-upgrade-banner">
          <div className="pg-upgrade-banner__content">
            <span className="pg-upgrade-banner__icon">✦</span>
            <div>
              <strong className="pg-upgrade-banner__title">AI Enrichment</strong>
              <p className="pg-upgrade-banner__desc">
                Automatically research and enrich every place with descriptions,
                tips, seasonality, and more. Available on the Concierge plan.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate("settings?tab=billing")}
            className="sf-btn sf-btn--primary sf-btn--sm pg-upgrade-banner__cta"
          >
            Upgrade to Concierge
          </button>
        </div>
      )}

      {/* Enrichment status strip */}
      {canEnrich && places.length > 0 && (
        <EnrichmentStatusStrip
          research={research}
          onViewDetails={() => setShowDrawer(true)}
        />
      )}

      {/* Research error */}
      {canEnrich && research.researchError && (
        <div className="login-card__error mb-4" role="alert">
          {research.researchError}
        </div>
      )}

      {/* Search + Filters */}
      <div className="places-filter-bar">
        <input
          type="search"
          placeholder="Search by name, area..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search places"
          className="login-card__input places-filter-bar__search"
        />
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          aria-label="Filter by type"
          className="places-filter-bar__select"
        >
          <option value="">All types</option>
          {types.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          value={areaFilter}
          onChange={(e) => setAreaFilter(e.target.value)}
          aria-label="Filter by area"
          className="places-filter-bar__select"
        >
          <option value="">All areas</option>
          {areas.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        {hasActiveFilters && (
          <button
            className="sf-btn sf-btn--ghost sf-btn--sm"
            onClick={clearAllFilters}
          >
            Clear filters
          </button>
        )}
      </div>

      {showAddModal && (
        <AddPlaceInline
          existingIds={new Set(places.map((p) => p.id))}
          onAdd={handleAddPlace}
          onClose={() => setShowAddModal(false)}
          saving={saving}
        />
      )}

      {loading ? (
        <div className="p-8 text-center">
          <div className="sf-spinner" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__title">
            {hasActiveFilters ? "No matching places" : "No places yet"}
          </div>
          <div className="empty-state__desc">
            {hasActiveFilters
              ? "Try adjusting your search or filters."
              : "Add places to build your destination guide."}
          </div>
          {hasActiveFilters && (
            <button
              className="sf-btn sf-btn--ghost mt-3 text-sm"
              onClick={clearAllFilters}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className={`grid gap-5 ${selected ? "grid-cols-1 lg:grid-cols-[1fr_380px]" : "grid-cols-1"}`}>
          <div className="section-card p-0 overflow-hidden overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <SortableTh column="name" current={sortColumn} dir={sortDir} onSort={handleSort}>Name</SortableTh>
                  <SortableTh column="type" current={sortColumn} dir={sortDir} onSort={handleSort}>Type</SortableTh>
                  <SortableTh column="area" current={sortColumn} dir={sortDir} onSort={handleSort}>Area</SortableTh>
                  <th>Seasonality</th>
                  <SortableTh column="quality" current={sortColumn} dir={sortDir} onSort={handleSort}>Quality</SortableTh>
                  {canEnrich && <SortableTh column="ai" current={sortColumn} dir={sortDir} onSort={handleSort}>AI</SortableTh>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((place) => {
                  const completeness = computeCompleteness(place)
                  return (
                    <tr
                      key={place.id}
                      onClick={() => handleSelectRow(place.id)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleSelectRow(place.id) } }}
                      tabIndex={0}
                      role="button"
                      aria-pressed={place.id === selectedId}
                      className={`cursor-pointer ${place.id === selectedId ? "bg-primary/10" : ""}`}
                    >
                      <td>
                        <div className="font-medium truncate">{placeName(place)}</div>
                      </td>
                      <td>
                        <TypeBadge value={place.type} />
                      </td>
                      <td className="text-muted-foreground text-sm">
                        {place.area || "-"}
                      </td>
                      <td>
                        {place.seasonality ? (
                          <SeasonalityBadge value={place.seasonality} />
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td>
                        <QualityPill completeness={completeness} />
                      </td>
                      {canEnrich && (
                        <td>
                          <AiStatusCell
                            placeId={place.id}
                            status={deriveAiStatus(research.getItemForPlace(place.id), research.inProgress, research.progress, completeness.variant === "healthy")}
                            isEnriching={research.enrichingPlaceIds.has(place.id)}
                            onEnrich={research.handleEnrichPlace}
                            batchInProgress={research.inProgress}
                          />
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {selected && (
            <PlaceInspector
              place={selected}
              saving={saving}
              onClose={() => setSelectedId(null)}
              onDelete={handleDelete}
              onUpdate={handleUpdatePlace}
              researchItem={canEnrich ? research.getItemForPlace(selected.id) : undefined}
              onRetryEnrich={canEnrich ? () => research.handleEnrichPlace(selected.id) : undefined}
              canEnrich={canEnrich}
            />
          )}
        </div>
      )}

      {/* Run details drawer */}
      {canEnrich && showDrawer && (
        <RunDetailsDrawer
          research={research}
          onClose={() => setShowDrawer(false)}
          onSelectPlace={(placeId) => { setSelectedId(placeId); setShowDrawer(false) }}
        />
      )}

      <CopyConfigModal
        isOpen={showCopyModal}
        sourcePropertyId={propertyId}
        sourcePropertyName={property?.name || "Current property"}
        configType="places"
        properties={properties.map((p) => ({ id: p.id, name: p.name }))}
        onClose={() => setShowCopyModal(false)}
      />
    </div>
  )
}

// ─── Enrichment Status Strip ─────────────────────────────────────────────────

interface EnrichmentStatusStripProps {
  research: ReturnType<typeof useResearchState>
  onViewDetails: () => void
}

function EnrichmentStatusStrip({ research, onViewDetails }: EnrichmentStatusStripProps) {
  const { report, inProgress, progress, researchLoading } = research
  const summary = report?.summary
  const hasErrors = (summary?.failed_places || 0) > 0
  const [collapsed, setCollapsed] = useState(!inProgress && !hasErrors)

  // Auto-expand when running or errors
  useEffect(() => {
    if (inProgress || hasErrors) setCollapsed(false)
  }, [inProgress, hasErrors])

  if (researchLoading) return null

  // No report yet
  if (!report) {
    return (
      <div className="enrichment-strip mb-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Enrich places to fill missing details automatically
          </span>
        </div>
      </div>
    )
  }

  // Running state
  if (inProgress) {
    return (
      <div className="enrichment-strip enrichment-strip--running mb-4">
        <div className="flex items-center gap-3">
          <div className="sf-spinner sf-spinner--xs" />
          <div className="flex-1">
            <div className="text-sm font-medium">Enriching places...</div>
            {progress && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {String(progress.processed_places || 0)} / {String(progress.total_places || "?")} places
                {progress.next_place_name ? (
                  <> &middot; Researching: <strong>{String(progress.next_place_name)}</strong></>
                ) : null}
              </div>
            )}
          </div>
          <button
            className="sf-btn sf-btn--ghost sf-btn--sm"
            onClick={research.handleStop}
          >
            Stop
          </button>
        </div>
      </div>
    )
  }

  // Completed — collapsed
  if (collapsed) {
    const finishedAgo = report.finished_at ? timeAgo(report.finished_at) : null
    return (
      <div className="enrichment-strip mb-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {finishedAgo ? `Last enriched ${finishedAgo}` : "Enrichment completed"}
            {summary && <> &middot; {summary.completed_places}/{summary.total_places} places</>}
            {hasErrors && (
              <span className="text-destructive font-medium">
                {" "}&middot; {summary?.failed_places} error{(summary?.failed_places || 0) !== 1 ? "s" : ""}
              </span>
            )}
          </span>
          <div className="flex gap-2">
            <button
              className="sf-btn sf-btn--ghost sf-btn--sm"
              onClick={() => setCollapsed(false)}
            >
              Expand
            </button>
            <button
              className="sf-btn sf-btn--ghost sf-btn--sm"
              onClick={onViewDetails}
            >
              View details
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Completed — expanded
  return (
    <div className={`enrichment-strip${hasErrors ? " enrichment-strip--error" : ""} mb-4`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          {hasErrors ? (
            <div className="text-sm font-medium text-destructive">
              {summary?.failed_places} place{(summary?.failed_places || 0) !== 1 ? "s" : ""} need attention
            </div>
          ) : (
            <div className="text-sm font-medium">Enrichment complete</div>
          )}
          {report.finished_at && (
            <div className="text-xs text-muted-foreground mt-0.5">
              Finished {timeAgo(report.finished_at)}
            </div>
          )}
        </div>
        <button
          className="sf-btn sf-btn--ghost sf-btn--sm"
          onClick={() => setCollapsed(true)}
        >
          Collapse
        </button>
      </div>
      {summary && (
        <div className="flex gap-4 text-xs text-muted-foreground flex-wrap mb-3">
          <span>{summary.completed_places}/{summary.total_places} researched</span>
          <span>{summary.places_with_enrichment} enriched</span>
          {report.auto_enrich?.fields_filled != null && <span>{report.auto_enrich.fields_filled} fields filled</span>}
          {summary.failed_places > 0 && (
            <span className="text-destructive">{summary.failed_places} errors</span>
          )}
        </div>
      )}
      <div className="flex gap-2">
        <button className="sf-btn sf-btn--ghost sf-btn--sm" onClick={onViewDetails}>
          View details
        </button>
        {hasErrors && (
          <button
            className="sf-btn sf-btn--ghost sf-btn--sm"
            onClick={() => research.handleRun("continue")}
          >
            Retry failed
          </button>
        )}
      </div>
    </div>
  )
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return "just now"
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

// ─── AI Status Pill ──────────────────────────────────────────────────────────

function AiStatusPill({ status }: { status: AiStatus }) {
  const config = AI_STATUS_CONFIG[status]
  if (status === "running") {
    return (
      <span className="status-pill status-pill--neutral gap-1">
        <span className="sf-spinner sf-spinner--xs w-2.5 h-2.5" />
        {config.label}
      </span>
    )
  }
  return (
    <span className={`status-pill status-pill--${config.variant}`}>
      <span className="status-pill__dot" />
      {config.label}
    </span>
  )
}

// ─── AI Status Cell (per-row enrichment button or status pill) ───────────────

function AiStatusCell({
  placeId, status, isEnriching, onEnrich, batchInProgress,
}: {
  placeId: string
  status: AiStatus
  isEnriching: boolean
  onEnrich: (placeId: string) => void
  batchInProgress: boolean
}) {
  if (isEnriching) {
    return (
      <span className="status-pill status-pill--neutral gap-1">
        <span className="sf-spinner sf-spinner--xs w-2.5 h-2.5" />
        Enriching
      </span>
    )
  }
  if (status === "up-to-date" || status === "running" || status === "queued") {
    return <AiStatusPill status={status} />
  }
  if (status === "error") {
    return (
      <div className="flex items-center gap-2">
        <AiStatusPill status={status} />
        <button
          className="sf-btn sf-btn--ghost sf-btn--sm"
          onClick={(e) => { e.stopPropagation(); onEnrich(placeId) }}
          disabled={batchInProgress}
        >
          Retry
        </button>
      </div>
    )
  }
  // needs-enrichment → show Enrich button
  return (
    <button
      className="sf-btn sf-btn--secondary sf-btn--sm"
      onClick={(e) => { e.stopPropagation(); onEnrich(placeId) }}
      disabled={batchInProgress}
    >
      Enrich
    </button>
  )
}

// ─── Stat Box ────────────────────────────────────────────────────────────────

function StatBox({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div className="places-stat-box">
      <div className="places-stat-box__label">{label}</div>
      <div className="places-stat-box__value">{value != null ? String(value) : "-"}</div>
    </div>
  )
}

// ─── Run Details Drawer ──────────────────────────────────────────────────────

interface RunDetailsDrawerProps {
  research: ReturnType<typeof useResearchState>
  onClose: () => void
  onSelectPlace: (placeId: string) => void
}

function RunDetailsDrawer({ research, onClose, onSelectPlace }: RunDetailsDrawerProps) {
  const { report, inProgress } = research
  const summary = report?.summary
  const autoEnrich = report?.auto_enrich
  const items = report?.items || []

  const [drawerSearch, setDrawerSearch] = useState("")
  const [drawerTypeFilter, setDrawerTypeFilter] = useState("")
  const [confirmRestart, setConfirmRestart] = useState(false)

  const types = useMemo(() => {
    const set = new Set<string>()
    for (const item of items) {
      if (item.type) set.add(item.type)
    }
    return Array.from(set).sort()
  }, [items])

  const filtered = useMemo(() => {
    let list = items
    if (drawerTypeFilter) list = list.filter(it => it.type === drawerTypeFilter)
    if (drawerSearch) {
      const q = drawerSearch.toLowerCase()
      list = list.filter(it =>
        it.place_name.toLowerCase().includes(q) ||
        it.area.toLowerCase().includes(q) ||
        it.place_id.toLowerCase().includes(q)
      )
    }
    return list
  }, [items, drawerTypeFilter, drawerSearch])

  return (
    <div className="drawer-overlay" onClick={onClose} role="presentation" aria-hidden="true">
      <div className="drawer drawer--wide" onClick={(e) => e.stopPropagation()}>
        <div className="drawer__header">
          <h3 className="drawer__title">Enrichment Details</h3>
          <button className="drawer__close" type="button" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>
        <div className="drawer__body">
          {/* Actions bar */}
          <div className="flex gap-2 mb-5">
            <button
              className="sf-btn sf-btn--ghost text-sm"
              onClick={() => research.handleRun("continue")}
              disabled={inProgress}
            >
              Retry failed
            </button>
            {!confirmRestart ? (
              <button
                className="sf-btn sf-btn--ghost text-sm text-destructive"
                onClick={() => setConfirmRestart(true)}
                disabled={inProgress}
              >
                Full restart
              </button>
            ) : (
              <div className="flex gap-2 items-center">
                <span className="text-xs text-muted-foreground">
                  Re-research all places from scratch?
                </span>
                <button
                  className="sf-btn sf-btn--destructive sf-btn--sm"
                  onClick={() => { research.handleRun("restart"); setConfirmRestart(false) }}
                >
                  Confirm
                </button>
                <button
                  className="sf-btn sf-btn--ghost sf-btn--sm"
                  onClick={() => setConfirmRestart(false)}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {/* Summary stats */}
          {summary && (
            <div className="grid grid-cols-[repeat(auto-fit,minmax(110px,1fr))] gap-3 mb-5">
              <StatBox label="Total Places" value={summary.total_places} />
              <StatBox label="Researched" value={summary.completed_places} />
              <StatBox label="Enriched" value={summary.places_with_enrichment} />
              <StatBox label="Fields Filled" value={autoEnrich?.fields_filled} />
              <StatBox label="Rules Updated" value={summary.places_with_rule_suggestions} />
              <StatBox label="Errors" value={summary.failed_places} />
            </div>
          )}

          {/* Timestamps */}
          {report && (
            <div className="flex gap-4 text-xs text-muted-foreground mb-5">
              {report.generated_at && <span>Started: {new Date(report.generated_at).toLocaleString()}</span>}
              {report.finished_at && <span>Finished: {new Date(report.finished_at).toLocaleString()}</span>}
            </div>
          )}

          {/* Per-place results */}
          {items.length > 0 && (
            <>
              <div className="flex justify-between items-center mb-3">
                <h4 className="m-0 text-base font-semibold">Per-Place Results</h4>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={drawerSearch}
                    onChange={e => setDrawerSearch(e.target.value)}
                    aria-label="Search places"
                    className="px-2 py-1 text-xs rounded-sm border border-border w-full md:w-[140px]"
                  />
                  <select
                    value={drawerTypeFilter}
                    onChange={e => setDrawerTypeFilter(e.target.value)}
                    aria-label="Filter by type"
                    className="px-2 py-1 text-xs rounded-sm border border-border"
                  >
                    <option value="">All types</option>
                    {types.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              <div className="data-table-wrapper">
                <table className="data-table data-table--compact">
                  <thead>
                    <tr>
                      <th>Place</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Enrichment</th>
                      <th>Confidence</th>
                      <th>Sources</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(item => {
                      const statusInfo = getResearchStatusInfo(item.status)
                      const { filled, total } = countEnrichmentFields(item.enrichment)
                      return (
                        <tr
                          key={item.place_id}
                          onClick={() => onSelectPlace(item.place_id)}
                          className="cursor-pointer"
                          tabIndex={0}
                          onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelectPlace(item.place_id) } }}
                        >
                          <td className="font-medium text-sm">
                            {item.place_name}
                            <div className="text-xs text-muted-foreground">{item.area}</div>
                          </td>
                          <td className="text-xs">{item.type}</td>
                          <td>
                            <span className={`status-pill status-pill--${statusInfo.variant}`}>
                              {statusInfo.label}
                            </span>
                          </td>
                          <td className="text-sm">
                            {item.status === "ok" && (item.enrichment?.enrichment_confidence || 0) > 0 ? (
                              <span>{filled}/{total}</span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="text-sm">
                            {(() => {
                              const conf = item.enrichment?.enrichment_confidence || item.confidence || 0
                              return conf > 0 ? (
                                <span>{(conf * 100).toFixed(0)}%</span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )
                            })()}
                          </td>
                          <td className="text-sm">{item.source_urls?.length || 0}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {filtered.length === 0 && items.length > 0 && (
                <div className="text-center p-4 text-muted-foreground text-sm">
                  No places match the current filters.
                  {(drawerTypeFilter || drawerSearch) && (
                    <button
                      onClick={() => { setDrawerTypeFilter(""); setDrawerSearch("") }}
                      className="bg-transparent border-none text-primary cursor-pointer ml-2"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          {items.length === 0 && !report && (
            <div className="text-center p-8 text-muted-foreground">
              <div className="font-medium mb-2">No enrichment data</div>
              <div className="text-sm">
                Run enrichment to discover and fill venue data for your guide.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Inspector ──────────────────────────────────────────────────────────────

interface EditForm {
  enName: string
  area: string
  type: string
  seasonality: string
  enDescription: string
  hostRecommendation: string
  lat: number | null
  lng: number | null
  // AI-extended: recommendation text
  enShort: string
  enWhy: string
  enTip: string
  enRainAlternative: string
  enBestFor: string
  // AI-extended: who it's for
  suitableFor: string[]
  familyFriendly: "yes" | "no" | "unset"
  accessibility: string
  // AI-extended: tags
  tagsRaw: string
}

function placeToEditForm(place: Place): EditForm {
  const en = (place.en || {}) as Record<string, unknown>
  return {
    enName: place.en?.name || "",
    area: place.area || "",
    type: place.type || "other",
    seasonality: place.seasonality || "year-round",
    enDescription: place.en?.description || "",
    hostRecommendation: place.host_recommendation || "",
    lat: place.coordinates?.lat ?? null,
    lng: place.coordinates?.lng ?? null,
    enShort: (en.short as string) || "",
    enWhy: (en.why as string) || "",
    enTip: (en.tip as string) || "",
    enRainAlternative: (en.rainAlternative as string) || "",
    enBestFor: (en.bestFor as string) || "",
    suitableFor: Array.isArray(place.suitableFor) ? [...(place.suitableFor as string[])] : [],
    familyFriendly: place.familyFriendly === true ? "yes" : place.familyFriendly === false ? "no" : "unset",
    accessibility: (place.accessibility as string) || "",
    tagsRaw: Array.isArray(place.tags) ? (place.tags as string[]).join(", ") : "",
  }
}

function editFormToFields(form: EditForm): Partial<Place> {
  const en: Record<string, unknown> = {
    name: form.enName.trim(),
    description: form.enDescription.trim() || undefined,
  }
  if (form.enShort.trim()) en.short = form.enShort.trim()
  if (form.enWhy.trim()) en.why = form.enWhy.trim()
  if (form.enTip.trim()) en.tip = form.enTip.trim()
  if (form.enRainAlternative.trim()) en.rainAlternative = form.enRainAlternative.trim()
  if (form.enBestFor.trim()) en.bestFor = form.enBestFor.trim()

  const fields: Partial<Place> = {
    area: form.area.trim(),
    type: form.type,
    seasonality: form.seasonality,
    en: en as Place["en"],
  }

  fields.host_recommendation = form.hostRecommendation.trim() ? form.hostRecommendation.trim().slice(0, 1200) : ""

  if (form.lat != null && form.lng != null) {
    fields.coordinates = { lat: form.lat, lng: form.lng }
  }

  // Who it's for
  fields.suitableFor = form.suitableFor.length > 0 ? form.suitableFor : undefined
  fields.familyFriendly = form.familyFriendly === "yes" ? true : form.familyFriendly === "no" ? false : undefined
  if (form.accessibility) fields.accessibility = form.accessibility

  // Tags — normalize: trim, lowercase, deduplicate
  const tags = form.tagsRaw.split(",").map(t => t.trim().toLowerCase()).filter(Boolean)
  fields.tags = tags.length > 0 ? [...new Set(tags)] : undefined

  return fields
}

function PlaceInspector({
  place,
  saving,
  onClose,
  onDelete,
  onUpdate,
  researchItem,
  onRetryEnrich,
  canEnrich = true,
}: {
  place: Place
  saving: boolean
  onClose: () => void
  onDelete: (id: string) => void
  onUpdate: (id: string, fields: Partial<Place>) => Promise<boolean | undefined>
  researchItem?: ResearchItem | null
  onRetryEnrich?: () => void
  canEnrich?: boolean
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState<EditForm>(() => placeToEditForm(place))
  const [confirmDelete, setConfirmDelete] = useState(false)
  const initialFormRef = useRef<EditForm>(placeToEditForm(place))

  // Reset form when selected place changes
  useEffect(() => {
    const newForm = placeToEditForm(place)
    setEditForm(newForm)
    initialFormRef.current = newForm
    setIsEditing(false)
    setConfirmDelete(false)
  }, [place.id])

  const isDirty = isEditing && JSON.stringify(editForm) !== JSON.stringify(initialFormRef.current)

  const handleStartEdit = () => {
    const form = placeToEditForm(place)
    setEditForm(form)
    initialFormRef.current = form
    setIsEditing(true)
    setConfirmDelete(false)
  }

  const handleCancelEdit = () => {
    setEditForm(initialFormRef.current)
    setIsEditing(false)
  }

  const handleSave = async () => {
    const fields = editFormToFields(editForm)
    const ok = await onUpdate(place.id, fields)
    if (ok) {
      setIsEditing(false)
      initialFormRef.current = editForm
    }
  }

  const handleClose = () => {
    if (isDirty) {
      // Show inline unsaved prompt instead of closing
      return
    }
    onClose()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      if (isEditing) {
        handleCancelEdit()
      } else {
        onClose()
      }
    }
  }

  // Removed inline style objects — using CSS classes directly

  return (
    <div className="section-card" onKeyDown={handleKeyDown}>
      {/* Header */}
      <div className="inspector__header">
        <h3 className="inspector__title">{placeName(place)}</h3>
        <div className="flex gap-2 items-center">
          {!isEditing && (
            <button
              className="sf-btn sf-btn--ghost"
              onClick={handleStartEdit}
              type="button"
            >
              Edit
            </button>
          )}
          <button className="inspector__close" onClick={handleClose} type="button" aria-label="Close inspector">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>
      </div>

      {/* Unsaved changes bar */}
      {isDirty && (
        <div className="flex items-center justify-between px-3 py-2 mb-4 bg-warning/10 border border-warning rounded-sm text-xs font-medium">
          <span>Unsaved changes</span>
          <div className="flex gap-2">
            <button
              className="sf-btn sf-btn--ghost sf-btn--sm"
              onClick={handleCancelEdit}
            >
              Discard
            </button>
            <button
              className="sf-btn sf-btn--primary sf-btn--sm"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving\u2026" : "Save"}
            </button>
          </div>
        </div>
      )}

      {/* Fields */}
      <div className="flex flex-col gap-4">
        <Field label="ID" value={place.id} />

        {isEditing ? (
          <>
            <FormField label="Name (EN) *">
              <input
                type="text"
                required
                value={editForm.enName}
                onChange={(e) => setEditForm((f) => ({ ...f, enName: e.target.value }))}
                className="login-card__input h-9 text-sm"
                autoFocus
              />
            </FormField>

            <FormField label="Area">
              <input
                type="text"
                value={editForm.area}
                onChange={(e) => setEditForm((f) => ({ ...f, area: e.target.value }))}
                className="login-card__input h-9 text-sm"
              />
            </FormField>

            <div className="grid grid-cols-2 gap-3">
              <FormField label="Type">
                <select
                  value={editForm.type}
                  onChange={(e) => setEditForm((f) => ({ ...f, type: e.target.value }))}
                  className="sf-field-select"
                >
                  {PLACE_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </FormField>

              <FormField label="Seasonality">
                <select
                  value={editForm.seasonality}
                  onChange={(e) => setEditForm((f) => ({ ...f, seasonality: e.target.value }))}
                  className="sf-field-select"
                >
                  {SEASONALITY_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </FormField>
            </div>

            <FormField label="Description (EN)">
              <textarea
                value={editForm.enDescription}
                onChange={(e) => setEditForm((f) => ({ ...f, enDescription: e.target.value }))}
                rows={3}
                className="sf-field-textarea"
                placeholder="Brief description of this place\u2026"
              />
            </FormField>

            <FormField label="Host recommendation" hint={HOST_REC_HINT}>
              <textarea
                value={editForm.hostRecommendation}
                onChange={(e) => setEditForm((f) => ({ ...f, hostRecommendation: e.target.value.slice(0, 1200) }))}
                rows={3}
                className="sf-field-textarea"
                placeholder="Your personal tip for guests visiting this place\u2026"
              />
              <div className="text-xs text-muted-foreground text-right mt-0.5">
                {editForm.hostRecommendation.length}/1200
              </div>
            </FormField>

            {editForm.lat != null && editForm.lng != null && (
              <Field label="Coordinates" value={`${editForm.lat}, ${editForm.lng}`} />
            )}

            {/* AI-extended fields */}
            <AiFieldsSection editForm={editForm} setEditForm={setEditForm} />
          </>
        ) : (
          <>
            <Field label="Type" value={place.type} />
            <Field label="Area" value={place.area} />
            <Field label="Seasonality" value={place.seasonality} />
            <Field label="Name (EN)" value={place.en?.name} />
            <Field label="Description (EN)" value={place.en?.description} />
            <Field label="Host recommendation" value={place.host_recommendation} />
            {place.coordinates && (place.coordinates.lat != null || place.coordinates.lng != null) && (
              <Field
                label="Coordinates"
                value={`${place.coordinates.lat ?? "?"}, ${place.coordinates.lng ?? "?"}`}
              />
            )}

            {/* AI Preview */}
            {canEnrich && <AiPreview place={place} researchItem={researchItem} />}

            {/* Completeness Breakdown */}
            <CompletenessBreakdown place={place} />

            {/* AI Enrichment Results */}
            {canEnrich && researchItem && (
              <InspectorEnrichmentSection
                item={researchItem}
                place={place}
                onApply={(placeKey, value) => onUpdate(place.id, buildFieldUpdate(placeKey, value))}
                onRetry={onRetryEnrich}
              />
            )}
          </>
        )}
      </div>

      {/* Footer actions */}
      {isEditing ? (
        <div className="flex gap-2 mt-5 justify-end">
          <button
            className="sf-btn sf-btn--ghost"
            onClick={handleCancelEdit}
          >
            Cancel
          </button>
          <button
            className="sf-btn sf-btn--primary"
            onClick={handleSave}
            disabled={saving || !editForm.enName.trim()}
          >
            {saving ? "Saving\u2026" : "Save changes"}
          </button>
        </div>
      ) : (
        <div className="mt-6 border-t border-border pt-4">
          {confirmDelete ? (
            <div className="flex gap-2">
              <button
                className="sf-btn sf-btn--destructive flex-1"
                onClick={() => onDelete(place.id)}
                disabled={saving}
              >
                {saving ? "Removing\u2026" : "Confirm remove"}
              </button>
              <button
                className="sf-btn sf-btn--ghost"
                onClick={() => setConfirmDelete(false)}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              className="sf-btn sf-btn--ghost text-destructive w-full text-sm"
              onClick={() => setConfirmDelete(true)}
            >
              Remove place
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Add Place Inline Form ───────────────────────────────────────────────────

const EMPTY_EDIT_FORM: EditForm = {
  enName: "", area: "", type: "restaurant", seasonality: "year-round",
  enDescription: "", hostRecommendation: "", lat: null, lng: null,
  enShort: "", enWhy: "", enTip: "", enRainAlternative: "", enBestFor: "",
  suitableFor: [], familyFriendly: "unset", accessibility: "", tagsRaw: "",
}

function AddPlaceInline({
  existingIds,
  onAdd,
  onClose,
  saving,
}: {
  existingIds: Set<string>
  onAdd: (place: Place) => void
  onClose: () => void
  saving: boolean
}) {
  const [form, setForm] = useState<EditForm>({ ...EMPTY_EDIT_FORM })
  const [searchQuery, setSearchQuery] = useState("")
  const [searchResults, setSearchResults] = useState<PlaceSearchResult[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchError, setSearchError] = useState("")
  const [showSearch, setShowSearch] = useState(false)

  // Form fields use CSS classes: sf-field-select, sf-field-textarea, login-card__input

  const handlePlaceSearch = async () => {
    const q = searchQuery.trim()
    if (!q) return
    setSearchLoading(true)
    setSearchError("")
    const res = await apiClient.placeSearch(q)
    setSearchLoading(false)
    if (res.ok && res.results) {
      setSearchResults(res.results as unknown as PlaceSearchResult[])
      if (res.results.length === 0) setSearchError("No results found. Try a different query.")
    } else {
      setSearchError("Search failed. Try again.")
    }
  }

  const handleSelectSearchResult = (result: PlaceSearchResult) => {
    setForm((f) => ({
      ...f,
      enName: result.name || f.enName,
      area: areaFromAddress(result.address) || f.area,
      type: mapPlaceType(result.type),
      lat: result.lat,
      lng: result.lng,
    }))
    setShowSearch(false)
    setSearchResults([])
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const id = slugify(form.enName, existingIds)
    const fields = editFormToFields(form)
    const place: Place = { id, ...fields } as Place
    onAdd(place)
  }

  return (
    <div className="section-card mb-5">
      <div className="inspector__header mb-4">
        <h3 className="inspector__title">Add place</h3>
        <button className="inspector__close" onClick={onClose} type="button" aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
        </button>
      </div>

      {/* Place search */}
      <div className="mb-4">
        {!showSearch ? (
          <button
            type="button"
            className="sf-btn sf-btn--ghost text-sm"
            onClick={() => setShowSearch(true)}
          >
            Search for a place
          </button>
        ) : (
          <div className="p-3 border border-border rounded-sm bg-muted">
            <div className="flex gap-2 mb-2">
              <input
                type="search"
                placeholder="e.g. The Corner Café"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handlePlaceSearch() } }}
                className="login-card__input flex-1 h-9 text-sm"
                autoFocus
              />
              <button
                type="button"
                className="sf-btn sf-btn--primary h-9 whitespace-nowrap"
                onClick={handlePlaceSearch}
                disabled={searchLoading || !searchQuery.trim()}
              >
                {searchLoading ? "Searching\u2026" : "Search"}
              </button>
              <button type="button" className="sf-btn sf-btn--ghost h-9" onClick={() => { setShowSearch(false); setSearchResults([]) }}>
                Close
              </button>
            </div>
            {searchError && (
              <p className="text-sm text-destructive mb-2">{searchError}</p>
            )}
            {searchResults.length > 0 && (
              <div className="flex flex-col gap-0.5">
                {searchResults.map((result) => (
                  <button
                    key={result.place_id}
                    type="button"
                    onClick={() => handleSelectSearchResult(result)}
                    className="places-search-result"
                  >
                    <div className="places-search-result__name">{result.name || result.display_name}</div>
                    <div className="places-search-result__detail">{result.display_name}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-4">
          <FormField label="Name (EN) *">
            <input
              type="text"
              required
              value={form.enName}
              onChange={(e) => setForm((f) => ({ ...f, enName: e.target.value }))}
              className="login-card__input h-9 text-sm"
              placeholder="Bistro Perspektiv"
            />
          </FormField>

          <FormField label="Area">
            <input
              type="text"
              value={form.area}
              onChange={(e) => setForm((f) => ({ ...f, area: e.target.value }))}
              className="login-card__input h-9 text-sm"
              placeholder="e.g. Downtown"
            />
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Type">
              <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} className="sf-field-select">
                {PLACE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </FormField>
            <FormField label="Seasonality">
              <select value={form.seasonality} onChange={(e) => setForm((f) => ({ ...f, seasonality: e.target.value }))} className="sf-field-select">
                {SEASONALITY_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </FormField>
          </div>

          <FormField label="Description (EN)">
            <textarea
              value={form.enDescription}
              onChange={(e) => setForm((f) => ({ ...f, enDescription: e.target.value }))}
              rows={2}
              className="sf-field-textarea"
              placeholder="Brief description of this place\u2026"
            />
          </FormField>

          <FormField label="Host recommendation" hint={HOST_REC_HINT}>
            <textarea
              value={form.hostRecommendation}
              onChange={(e) => setForm((f) => ({ ...f, hostRecommendation: e.target.value.slice(0, 1200) }))}
              rows={2}
              className="sf-field-textarea"
              placeholder="Your personal tip for guests visiting this place\u2026"
            />
            <div className="text-xs text-muted-foreground text-right mt-0.5">
              {form.hostRecommendation.length}/1200
            </div>
          </FormField>

          {form.lat != null && form.lng != null && (
            <Field label="Coordinates" value={`${form.lat}, ${form.lng}`} />
          )}

          {/* AI-extended fields */}
          <AiFieldsSection editForm={form} setEditForm={setForm} />
        </div>

        <div className="flex gap-2 mt-5 justify-end">
          <button type="button" className="sf-btn sf-btn--ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="sf-btn sf-btn--primary" disabled={saving || !form.enName.trim()}>
            {saving ? "Saving\u2026" : "Add place"}
          </button>
        </div>
      </form>
    </div>
  )
}

// ─── AI Fields Section (shared between inspector edit and add form) ──────────

function AiFieldsSection({
  editForm,
  setEditForm,
}: {
  editForm: EditForm
  setEditForm: React.Dispatch<React.SetStateAction<EditForm>>
}) {
  const [showRec, setShowRec] = useState(true)
  const [showWho, setShowWho] = useState(false)
  const [showTags, setShowTags] = useState(false)

  const whoCount = editForm.suitableFor.length + (editForm.familyFriendly !== "unset" ? 1 : 0) + (editForm.accessibility ? 1 : 0) + (editForm.enBestFor ? 1 : 0)
  const tagCount = editForm.tagsRaw.split(",").filter(t => t.trim()).length

  const toggleSuitableFor = (val: string) => {
    setEditForm((f) => ({
      ...f,
      suitableFor: f.suitableFor.includes(val)
        ? f.suitableFor.filter(v => v !== val)
        : [...f.suitableFor, val],
    }))
  }

  return (
    <div className="border-t border-border pt-4 mt-2">
      <div className="text-xs font-semibold text-muted-foreground mb-3">
        AI context fields
      </div>

      {/* Group 1: Recommendation text */}
      <CollapsibleGroup
        title="Recommendation text"
        open={showRec}
        onToggle={() => setShowRec(!showRec)}
      >
        <FormField label="Short description" hint="One-liner for the AI prompt">
          <input
            type="text"
            value={editForm.enShort}
            onChange={(e) => setEditForm((f) => ({ ...f, enShort: e.target.value }))}
            className="login-card__input h-9 text-sm"
            placeholder="Cozy café with great pastries"
          />
        </FormField>
        <FormField label="Why go" hint="Tells the AI why to recommend this place">
          <input
            type="text"
            value={editForm.enWhy}
            onChange={(e) => setEditForm((f) => ({ ...f, enWhy: e.target.value }))}
            className="login-card__input h-9 text-sm"
            placeholder="Best coffee in the neighbourhood"
          />
        </FormField>
        <FormField label="Tip" hint="Practical tip shown to guests">
          <input
            type="text"
            value={editForm.enTip}
            onChange={(e) => setEditForm((f) => ({ ...f, enTip: e.target.value }))}
            className="login-card__input h-9 text-sm"
            placeholder="Book a window table at sunset"
          />
        </FormField>
        <FormField label="Rain alternative" hint="Indoor/bad-weather option">
          <input
            type="text"
            value={editForm.enRainAlternative}
            onChange={(e) => setEditForm((f) => ({ ...f, enRainAlternative: e.target.value }))}
            className="login-card__input h-9 text-sm"
            placeholder="Cozy indoor seating available"
          />
        </FormField>
      </CollapsibleGroup>

      {/* Group 2: Who it's for */}
      <CollapsibleGroup
        title="Who it\u2019s for"
        badge={whoCount > 0 ? whoCount : undefined}
        open={showWho}
        onToggle={() => setShowWho(!showWho)}
      >
        <FormField label="Suitable for" hint="Affects which guest types see this place">
          <div className="flex flex-wrap gap-2">
            {SUITABLE_FOR_OPTIONS.map((opt) => (
              <label key={opt} className="flex items-center gap-1 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={editForm.suitableFor.includes(opt)}
                  onChange={() => toggleSuitableFor(opt)}
                />
                {opt}
              </label>
            ))}
          </div>
        </FormField>
        <FormField label="Family friendly" hint="Major impact on family recommendations">
          <div className="flex gap-3">
            {(["yes", "no", "unset"] as const).map((val) => (
              <label key={val} className="flex items-center gap-1 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="familyFriendly"
                  checked={editForm.familyFriendly === val}
                  onChange={() => setEditForm((f) => ({ ...f, familyFriendly: val }))}
                />
                {val === "yes" ? "Yes" : val === "no" ? "No" : "Not set"}
              </label>
            ))}
          </div>
          {editForm.familyFriendly === "no" && (
            <div className="text-xs text-destructive mt-1">
              The AI will avoid recommending this to families with children.
            </div>
          )}
        </FormField>
        <FormField label="Accessibility" hint="Used for families with children and multigenerational groups">
          <select
            value={editForm.accessibility}
            onChange={(e) => setEditForm((f) => ({ ...f, accessibility: e.target.value }))}
            className="sf-field-select"
          >
            <option value="">Not set</option>
            {ACCESSIBILITY_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt.replace(/_/g, " ")}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Best for" hint="Used for couple/romantic scoring">
          <input
            type="text"
            value={editForm.enBestFor}
            onChange={(e) => setEditForm((f) => ({ ...f, enBestFor: e.target.value }))}
            className="login-card__input h-9 text-sm"
            placeholder="Romantic dinner, date night"
          />
        </FormField>
      </CollapsibleGroup>

      {/* Group 3: Tags */}
      <CollapsibleGroup
        title="Tags"
        badge={tagCount > 0 ? tagCount : undefined}
        open={showTags}
        onToggle={() => setShowTags(!showTags)}
      >
        <FormField label="Tags" hint="Comma-separated. Helps the AI match to guest preferences.">
          <textarea
            value={editForm.tagsRaw}
            onChange={(e) => setEditForm((f) => ({ ...f, tagsRaw: e.target.value }))}
            rows={2}
            className="sf-field-textarea"
            placeholder="family, walk, date-night, seafood, outdoor"
          />
        </FormField>
      </CollapsibleGroup>
    </div>
  )
}

function CollapsibleGroup({
  title,
  badge,
  open,
  onToggle,
  children,
}: {
  title: string
  badge?: number
  open: boolean
  onToggle: () => void
  children: React.ReactNode
}) {
  return (
    <div className="mb-3">
      <button type="button" onClick={onToggle} className="places-collapsible__trigger">
        <span className={`places-collapsible__chevron${open ? " places-collapsible__chevron--open" : ""}`}>{"\u25B6"}</span>
        {title}
        {badge != null && badge > 0 && (
          <span className="places-collapsible__badge">{badge}</span>
        )}
      </button>
      {open && (
        <div className="places-collapsible__content">{children}</div>
      )}
    </div>
  )
}

// ─── Table Components ────────────────────────────────────────────────────────

function SortableTh({
  column,
  current,
  dir,
  onSort,
  children,
}: {
  column: SortColumn
  current: SortColumn | null
  dir: SortDir
  onSort: (col: SortColumn) => void
  children: React.ReactNode
}) {
  const active = current === column
  return (
    <th
      onClick={() => onSort(column)}
      className="cursor-pointer select-none whitespace-nowrap"
    >
      {children}
      <span className={`ml-1 text-xs ${active ? "opacity-100" : "opacity-30"}`}>
        {active ? (dir === "asc" ? "\u25B2" : "\u25BC") : "\u25B2"}
      </span>
    </th>
  )
}

function QualityPill({ completeness }: { completeness: CompletenessResult }) {
  return (
    <span className={`status-pill status-pill--${completeness.variant}`}>
      <span className="status-pill__dot" />
      {completeness.label}
    </span>
  )
}

// ─── Field Update Builder ────────────────────────────────────────────────────
// Converts an ENRICHMENT_FIELDS.placeKey like "en.short" or "suitableFor" into
// a Partial<Place> update that the existing handleUpdatePlace can apply.

function buildFieldUpdate(placeKey: string, value: unknown): Partial<Place> {
  if (placeKey.startsWith("en.")) {
    const subKey = placeKey.slice(3) // e.g. "short", "why", "tip"
    return { en: { [subKey]: value } as Place["en"] }
  }
  if (placeKey.startsWith("research_sources.")) {
    const subKey = placeKey.slice(17) // e.g. "official_site"
    return { research_sources: { [subKey]: value } } as Partial<Place>
  }
  return { [placeKey]: value } as Partial<Place>
}

// ─── Inspector Enrichment Section ────────────────────────────────────────────

function InspectorEnrichmentSection({
  item,
  place,
  onApply,
  onRetry,
}: {
  item: ResearchItem
  place: Place
  onApply: (placeKey: string, value: unknown) => Promise<boolean | undefined>
  onRetry?: () => void
}) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  const [applied, setApplied] = useState<Set<string>>(new Set())
  const [showRawResponse, setShowRawResponse] = useState(false)
  const [applying, setApplying] = useState<string | null>(null)

  // Reset local state when place changes
  const prevPlaceId = useRef(item.place_id)
  useEffect(() => {
    if (item.place_id !== prevPlaceId.current) {
      setDismissed(new Set())
      setApplied(new Set())
      setShowRawResponse(false)
      prevPlaceId.current = item.place_id
    }
  }, [item.place_id])

  const statusInfo = getResearchStatusInfo(item.status)
  const enrichment = item.enrichment
  const { filled, total } = countEnrichmentFields(enrichment)
  const isError = ERROR_STATUSES.has(item.status)
  const conf = enrichment?.enrichment_confidence || item.confidence || 0

  const handleApply = async (field: typeof ENRICHMENT_FIELDS[number], value: unknown) => {
    setApplying(field.key)
    const ok = await onApply(field.placeKey, value)
    if (ok) {
      setApplied(prev => new Set(prev).add(field.key))
    }
    setApplying(null)
  }

  return (
    <div className="mt-4 border-t border-border pt-4">
      <div className="text-xs font-semibold text-muted-foreground mb-3">
        AI Enrichment
      </div>

      {/* Status + confidence */}
      <div className="flex items-center gap-2 mb-3">
        <span className={`status-pill status-pill--${statusInfo.variant}`}>{statusInfo.label}</span>
        {conf > 0 && (
          <span className="text-xs text-muted-foreground">
            {(conf * 100).toFixed(0)}% confidence
          </span>
        )}
      </div>

      {/* Error state */}
      {isError && (
        <div className="p-3 bg-destructive/10 border border-destructive rounded-sm mb-3">
          <div className="text-sm font-medium text-destructive mb-2">
            Enrichment failed
          </div>
          <div className="text-xs text-muted-foreground mb-2">
            {getHumanReadableError(item.status)}
          </div>
          <div className="flex gap-2">
            {onRetry && (
              <button className="sf-btn sf-btn--ghost sf-btn--sm" onClick={onRetry}>
                Retry enrichment
              </button>
            )}
            {item.raw_llm_response && (
              <button
                className="sf-btn sf-btn--ghost sf-btn--sm"
                onClick={() => setShowRawResponse(!showRawResponse)}
              >
                {showRawResponse ? "Hide raw response" : "Show raw response"}
              </button>
            )}
          </div>
          {showRawResponse && item.raw_llm_response && (
            <pre className="text-xs bg-gray-50 p-3 rounded-sm overflow-auto max-h-[200px] whitespace-pre-wrap break-all mt-2">
              {item.raw_llm_response}
            </pre>
          )}
        </div>
      )}

      {/* Enrichment fields — Apply / Dismiss */}
      {enrichment && conf > 0 && (
        <>
          <div className="text-xs text-muted-foreground mb-2">
            {filled}/{total} fields suggested
          </div>
          <div className="flex flex-col gap-2">
            {ENRICHMENT_FIELDS.map((field) => {
              const val = enrichment[field.key]
              const display = formatEnrichmentValue(val)
              if (!display) return null
              if (dismissed.has(field.key)) return null

              const isApplied = applied.has(field.key)
              const isApplying = applying === field.key

              // Check if the place already has this value
              const currentValue = getPlaceFieldValue(place, field.placeKey)
              const currentDisplay = formatEnrichmentValue(currentValue)
              const alreadyMatches = currentDisplay === display

              return (
                <div key={field.key} className={`px-3 py-2 rounded-sm text-xs ${isApplied ? "bg-success/10" : "bg-muted"}`}>
                  <div className="font-medium text-muted-foreground mb-0.5">{field.label}</div>
                  <div className="text-sm mb-1">{display}</div>
                  {isApplied ? (
                    <span className="text-xs text-success">Applied</span>
                  ) : alreadyMatches ? (
                    <span className="text-xs text-muted-foreground">Already set</span>
                  ) : (
                    <div className="flex gap-1">
                      <button
                        className="sf-btn sf-btn--ghost text-xs h-[22px] px-2"
                        onClick={() => handleApply(field, val)}
                        disabled={isApplying}
                      >
                        {isApplying ? "Applying..." : "Apply"}
                      </button>
                      <button
                        className="sf-btn sf-btn--ghost text-xs h-[22px] px-2 text-muted-foreground"
                        onClick={() => setDismissed(prev => new Set(prev).add(field.key))}
                      >
                        Dismiss
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </>
      )}

      {/* Sources */}
      {item.source_urls?.length > 0 && (
        <div className="mt-3">
          <div className="text-xs font-medium text-muted-foreground mb-1">
            Sources ({item.source_urls.length})
          </div>
          <div className="flex flex-col gap-0.5 text-xs">
            {item.source_urls.map((url, i) => (
              <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="text-primary break-all">
                {url.length > 50 ? url.slice(0, 50) + "..." : url}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Key facts */}
      {item.new_information?.length > 0 && (
        <div className="mt-3">
          <div className="text-xs font-medium text-muted-foreground mb-1">
            Key facts
          </div>
          <ul className="m-0 pl-4 text-xs text-muted-foreground">
            {item.new_information.map((info, i) => (
              <li key={i} className="mb-0.5">{info}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Notes */}
      {item.notes && (
        <div className="text-xs text-muted-foreground italic mt-2">
          {item.notes}
        </div>
      )}
    </div>
  )
}

// Helper: Read a nested place field value by dot-path
function getPlaceFieldValue(place: Place, placeKey: string): unknown {
  const parts = placeKey.split(".")
  let current: unknown = place
  for (const part of parts) {
    if (current == null || typeof current !== "object") return undefined
    current = (current as Record<string, unknown>)[part]
  }
  return current
}

// ─── Inspector Sub-Components ────────────────────────────────────────────────

function computeSeasonFilter(place: Place): { status: "eligible" | "filtered" | "unknown"; reason?: string } {
  const now = new Date()
  const nowMonth = now.getMonth() + 1 // 1–12

  // Check explicit season_open months
  const seasonOpen = place.season_open as string | undefined
  if (seasonOpen) {
    const openMonths = seasonOpen.split(",").map((m: string) => parseInt(m, 10)).filter((n: number) => n > 0)
    if (openMonths.length > 0 && !openMonths.includes(nowMonth)) {
      const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]
      const openNames = openMonths.map((m: number) => monthNames[m - 1]).join(", ")
      return { status: "filtered", reason: `season_open: ${openNames} only — currently ${monthNames[nowMonth - 1]}` }
    }
  }

  // Check explicit winter_suitable
  const winterSuitable = place.winter_suitable as number | undefined
  if (winterSuitable === 0) {
    if (nowMonth <= 3 || nowMonth >= 11) {
      return { status: "filtered", reason: "winter_suitable: 0 — excluded Nov–Mar" }
    }
  }

  // Bridge from seasonality text field
  const seasonality = (place.seasonality as string || "").toLowerCase()
  if (seasonality === "summer" || seasonality === "summer-only" || seasonality === "summer-mainly") {
    const summerMonths = [5,6,7,8,9]
    if (!summerMonths.includes(nowMonth)) {
      const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]
      return { status: "filtered", reason: `seasonality: "${place.seasonality as string}" — open May–Sep only, currently ${monthNames[nowMonth - 1]}` }
    }
  }
  if (seasonality === "winter" || seasonality === "winter-only") {
    const winterMonths = [11,12,1,2,3]
    if (!winterMonths.includes(nowMonth)) {
      return { status: "filtered", reason: `seasonality: "${place.seasonality as string}" — open Nov–Mar only` }
    }
  }

  if (!seasonOpen && winterSuitable === undefined && !seasonality) {
    return { status: "unknown" }
  }
  return { status: "eligible" }
}

function AiPreview({ place, researchItem }: { place: Place; researchItem?: ResearchItem | null }) {
  const preview = formatPlaceLinePreview(place)
  const partyScores = computePartyScores(place)
  const seasonFilter = computeSeasonFilter(place)

  // Opening hours from research
  const openingHoursSummary = researchItem?.opening_hours_summary || ""
  const openingHoursApplicable = researchItem?.opening_hours_applicable

  return (
    <div className="mt-2">
      <div className="text-xs font-semibold text-muted-foreground mb-1">
        What the AI sees
      </div>
      <div className="places-ai-preview">{preview}</div>

      {/* Filtering status pills */}
      <div className="flex flex-col gap-1.5 mt-3">

        {/* Season filter status */}
        <div className="flex items-start gap-1.5 text-xs">
          <span className={`shrink-0 px-2 py-px rounded-full font-semibold text-[0.68rem] ${
            seasonFilter.status === "filtered"
              ? "bg-destructive/10 text-red-700"
              : seasonFilter.status === "eligible"
                ? "bg-success/10 text-green-700"
                : "bg-black/5 text-muted-foreground"
          }`}>
            {seasonFilter.status === "filtered" ? "Filtered now" : seasonFilter.status === "eligible" ? "In season" : "~ Season unknown"}
          </span>
          {seasonFilter.reason && (
            <span className="text-muted-foreground leading-snug">{seasonFilter.reason}</span>
          )}
          {!seasonFilter.reason && seasonFilter.status === "eligible" && (place.seasonality as string) && (
            <span className="text-muted-foreground">{place.seasonality as string}</span>
          )}
        </div>

        {/* Opening hours from research */}
        {openingHoursSummary ? (
          <div className="flex items-start gap-1.5 text-xs">
            <span className="shrink-0 px-2 py-px rounded-full font-semibold text-[0.68rem] bg-black/5 text-muted-foreground">Hours</span>
            <span className="text-muted-foreground leading-snug">{openingHoursSummary}</span>
          </div>
        ) : openingHoursApplicable === false ? (
          <div className="text-xs text-muted-foreground">
            Hours: not applicable (always open)
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">
            Hours: not researched yet
          </div>
        )}
      </div>

      {/* Party scores */}
      <div className="flex gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
        <span>Solo: {partyScores.solo >= 0 ? "+" : ""}{partyScores.solo}</span>
        <span>Couple: {partyScores.couple >= 0 ? "+" : ""}{partyScores.couple}</span>
        <span>Family: {partyScores.family >= 0 ? "+" : ""}{partyScores.family}</span>
        <span>Group: {partyScores.group >= 0 ? "+" : ""}{partyScores.group}</span>
      </div>
    </div>
  )
}

function CompletenessBreakdown({ place }: { place: Place }) {
  const completeness = computeCompleteness(place)
  if (completeness.missing.length === 0) return null

  return (
    <div className="mt-2">
      <div className="text-xs font-semibold text-muted-foreground mb-1">
        Data quality: {completeness.score}/{completeness.max}
      </div>
      <div className="flex flex-col gap-1">
        {completeness.missing.map((m) => (
          <div key={m.field} className="flex items-start gap-2 text-xs text-muted-foreground">
            <span className="text-warning shrink-0">{"\u25CB"}</span>
            <span>
              <strong className="text-foreground/70">{fieldDisplayName(m.field)}</strong>
              {" \u2014 "}
              {m.reason}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function fieldDisplayName(field: string): string {
  const names: Record<string, string> = {
    "host_recommendation": "Host recommendation",
    "en.description": "Description",
    "en.short": "Short description",
    "en.why": "Why go",
    "en.tip": "Tip",
    "coordinates": "Coordinates",
    "suitableFor": "Suitable for",
    "tags": "Tags",
    "familyFriendly": "Family friendly",
    "accessibility": "Accessibility",
  }
  return names[field] || field
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function mapPlaceType(type: string): string {
  const valid = new Set(PLACE_TYPES)
  return valid.has(type) ? type : "other"
}

function TypeBadge({ value }: { value: string }) {
  return <span className="places-badge places-badge--type">{value}</span>
}

function SeasonalityBadge({ value }: { value: string }) {
  return <span className={`places-badge places-badge--season-${value}`}>{value}</span>
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <div className="places-field__label">{label}</div>
      <div className={`places-field__value${!value ? " places-field__value--empty" : ""}`}>
        {value || "-"}
      </div>
    </div>
  )
}

function FormField({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="sf-field__label">{label}</label>
      {children}
      {hint && <div className="sf-field__hint">{hint}</div>}
    </div>
  )
}
